import Head from 'next/head';
import Link from 'next/link';
import { useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

const imgAirdropBoxStreamlineKameleon = '/assets/imgAirdropBoxStreamlineKameleon.svg';
const img2 = '/assets/img2.svg';
const img3 = '/assets/img3.svg';
const img4 = '/assets/img4.svg';
const img5 = '/assets/img5.svg';
const img6 = '/assets/img6.svg';
const img7 = '/assets/img7.svg';
const img8 = '/assets/img8.svg';
const img9 = '/assets/img9.svg';
const img10 = '/assets/img10.svg';
const img11 = '/assets/img11.svg';
const img12 = '/assets/img12.svg';

const featureCards = [
  {
    title: 'No LP Hunting',
    copy:
      'ZK-protected vault hides LP identities. Rewards can’t be extracted by MEV.',
    icon: img2,
  },
  {
    title: 'No Toxic Orderflow',
    copy:
      'Execution-layer privacy prevents orderflow sniping. RFQs settle at fair prices.',
    icon: img3,
  },
  {
    title: 'Higher Capital Efficiency',
    copy:
      'Adaptive credit pool aligns capital constraints to cross-chain demand in real time.',
    icon: img4,
  },
  {
    title: 'Private Execution',
    copy:
      'Only price + liquidity are revealed; identities and depth stay private.',
    icon: img5,
  },
];

const stackCards = [
  {
    title: 'Private-LP Vault (ZK-protected)',
    copy: [
      'LPs deposit USDC/ETH.',
      'Positions and orderbooks are fully private.',
      'Only price and available liquidity are visible.',
      'No participant can trace LP identity or depth.',
    ],
    accent: '#39e5f5',
    accentClass: 'bg-[#39e5f5]',
    hoverBgClass: 'hover:bg-[#0f1116]',
    icon: img6,
  },
  {
    title: 'ZK Execution Layer',
    copy: [
      'Swaps and quotes are posted on-chain as zk-proofs.',
      'Correctness is verified without revealing details.',
      'No need to trust intermediaries; order paths stay private.',
    ],
    accent: '#3377f5',
    accentClass: 'bg-[#3377f5]',
    hoverBgClass: 'hover:bg-[#0c2623]',
    icon: img7,
  },
  {
    title: 'Adaptive Credit Pool (PMM / x402)',
    copy: [
      'Capital constraints and risk models match liquidity across accounts.',
      'Traders get low-slippage RFQ pricing without revealing sources.',
    ],
    accent: '#9e4bf6',
    accentClass: 'bg-[#9e4bf6]',
    hoverBgClass: 'hover:bg-[#160d24]',
    icon: img8,
  },
  {
    title: 'Multi-chain Liquidity Layer',
    copy: [
      'Private liquidity from each chain is abstracted into a unified credit curve.',
      'L2, sidechains, and application chains are connected. Cross-chain trades include price + settlement proof.',
    ],
    accent: '#585bef',
    accentClass: 'bg-[#585bef]',
    hoverBgClass: 'hover:bg-[#0f1224]',
    icon: img9,
  },
];

const auditPoints = [
  'ZK-Proof circuits are audited by security experts',
  'Consensus proofs ensure correctness on-chain',
  'Execution environment is fully verified',
];

const metrics = [
  { label: 'Time to finalize trades', value: '~150 ms' },
  { label: 'Average Slippage', value: '< 0.005%' },
  { label: 'LP APR Range (Past 30d)', value: '8% - 15%' },
];

function HomePage() {
  const container = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      // Hero Section Animations
      const tl = gsap.timeline();

      tl.from('.hero-title-line', {
        y: 100,
        opacity: 0,
        duration: 0.8,
        stagger: 0.15,
        ease: 'power4.out',
        force3D: true, // Force GPU acceleration
      })
        .from('.hero-subtitle', {
          y: 30,
          opacity: 0,
          duration: 0.6,
          ease: 'power3.out',
          force3D: true, // Force GPU acceleration
        }, '-=0.4')
        .from('.hero-card', {
          y: 50,
          opacity: 0,
          duration: 0.6,
          ease: 'power3.out',
          force3D: true, // Force GPU acceleration
        }, '-=0.4');

      // Hero Background Smoke Effect - Optimized for performance
      // Use transform and opacity instead of filter for better performance
      const smokeTl = gsap.timeline({ repeat: -1, yoyo: true });

      // Main glow - optimized with transform instead of filter
      smokeTl.fromTo('.hero-glow',
        {
          scale: 1,
          opacity: 0.3,
        },
        {
          scale: 1.4,
          opacity: 0.8,
          duration: 5,
          ease: 'sine.inOut',
          force3D: true, // Force GPU acceleration
        }
      );

      // Secondary smoke - optimized with transform
      gsap.fromTo('.hero-smoke',
        {
          scale: 1.1,
          opacity: 0.2,
          x: 0,
          y: 0,
        },
        {
          scale: 1.5,
          opacity: 0.7,
          x: 40,
          y: -40,
          duration: 7,
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut',
          delay: 0.5,
          force3D: true, // Force GPU acceleration
        }
      );

      // Feature Cards Animation
      gsap.from('.feature-card', {
        scrollTrigger: {
          trigger: '.features-section',
          start: 'top 80%',
        },
        y: 50,
        opacity: 0,
        duration: 0.8,
        stagger: 0.1,
        ease: 'power3.out',
      });

      // Stack Cards Animation
      gsap.from('.stack-arrow', {
        scrollTrigger: {
          trigger: '.stack-section',
          start: 'top 75%',
        },
        opacity: 0,
        x: -20,
        duration: 0.6,
        stagger: 0.2,
        ease: 'power2.out',
      });

      gsap.from('.stack-card', {
        scrollTrigger: {
          trigger: '.stack-cards-container',
          start: 'top 85%',
        },
        x: -50,
        autoAlpha: 0,
        duration: 0.8,
        stagger: 0.2,
        ease: 'power3.out',
        clearProps: 'all' // Ensure props are cleared after animation to prevent stuck states
      });

      // Proof of Integrity Animation
      gsap.from('.audit-point', {
        scrollTrigger: {
          trigger: '.proof-section',
          start: 'top 75%',
        },
        x: -30,
        opacity: 0,
        duration: 0.6,
        stagger: 0.1,
        ease: 'power2.out',
      });

      // Metrics Counter Animation
      const metrics = document.querySelectorAll('.metric-value');
      metrics.forEach((metric) => {
        const valueText = metric.textContent || '';
        // Simple fade in for non-numeric or complex strings, could be enhanced for numbers
        gsap.from(metric, {
          scrollTrigger: {
            trigger: '.proof-section',
            start: 'top 75%',
          },
          scale: 0.5,
          opacity: 0,
          duration: 0.8,
          ease: 'back.out(1.7)',
        });
      });

      // Footer CTA Animation
      gsap.from('.footer-cta', {
        scrollTrigger: {
          trigger: '.footer-section',
          start: 'top 85%',
        },
        y: 50,
        opacity: 0,
        duration: 1,
        ease: 'power3.out',
      });
    },
    { scope: container }
  );

  return (
    <>
      <Head>
        <title>ZKPool</title>
      </Head>
      <div ref={container} className="relative min-h-screen overflow-x-hidden bg-[#04060b] text-white">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{
            backgroundImage:
              'radial-gradient(circle at 50% 60px, rgba(57,229,245,0.22), transparent 38%), radial-gradient(circle at 15% 10%, rgba(57,229,245,0.12), transparent 32%), radial-gradient(circle at 80% 0%, rgba(57,229,245,0.08), transparent 30%), linear-gradient(#0a1212 1px, transparent 1px), linear-gradient(90deg, #0a1212 1px, transparent 1px)',
            backgroundSize: '100% 100%, 100% 100%, 100% 100%, 84px 84px, 84px 84px',
          }}
        />

        <main className="relative mx-auto flex w-full flex-col px-0 pt-0 sm:px-0">
          <section className="relative left-1/2 flex min-h-[680px] w-screen -translate-x-1/2 flex-col items-center justify-center overflow-hidden px-4 text-center">
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(180deg, #07090e 0%, #05070c 45%, #040508 100%)',
              }}
            />
            <div
              className="absolute inset-0 opacity-80"
              style={{
                backgroundImage:
                  'linear-gradient(#0d1419 1px, transparent 1px), linear-gradient(90deg, #0d1419 1px, transparent 1px)',
                backgroundSize: '84px 84px',
              }}
            />
            <div
              className="hero-glow absolute inset-0 mix-blend-screen"
              style={{
                background:
                  'radial-gradient(circle at 50% 22%, rgba(69,233,248,0.4), rgba(4,6,11,0) 40%), radial-gradient(circle at 50% 35%, rgba(69,233,248,0.2), rgba(4,6,11,0) 60%)',
                filter: 'blur(40px)',
                willChange: 'transform, opacity', // Optimize for animations
                transform: 'translateZ(0)', // Force GPU layer
              }}
            />
            <div
              className="hero-smoke absolute inset-0 mix-blend-screen"
              style={{
                background:
                  'radial-gradient(circle at 40% 30%, rgba(57,229,245,0.15), rgba(4,6,11,0) 45%), radial-gradient(circle at 60% 25%, rgba(57,229,245,0.1), rgba(4,6,11,0) 50%)',
                filter: 'blur(50px)',
                willChange: 'transform, opacity', // Optimize for animations
                transform: 'translateZ(0)', // Force GPU layer
              }}
            />
            <div className="relative z-[1] mx-auto flex w-full max-w-[980px] flex-col items-center gap-10 px-4 py-16">
              <h1 className="text-center text-[110px] font-extrabold leading-[100%] text-[#39e5f5] [text-shadow:0_0_250px_var(--bg-brand,#39E5F5)] font-[var(--font-jakarta)]">
                <span className="hero-title-line block will-change-transform" style={{ transform: 'translateZ(0)' }}>ZKPool: Private</span>
                <span className="hero-title-line block will-change-transform" style={{ transform: 'translateZ(0)' }}>Liquidity</span>
                <span className="hero-title-line block will-change-transform" style={{ transform: 'translateZ(0)' }}>Infrastructure</span>
              </h1>
              <p className="hero-subtitle max-w-[720px] text-[18px] font-medium leading-[1.5] text-[#1dd0e6] font-[var(--font-jakarta)] will-change-transform" style={{ transform: 'translateZ(0)' }}>
                Generate professional-grade returns — without revealing your identity.
              </p>
              <div className="hero-card w-full max-w-[640px] rounded-[14px] bg-[#0e1421] px-8 py-6 text-left shadow-[0_16px_50px_rgba(0,0,0,0.5)] will-change-transform" style={{ transform: 'translateZ(0)' }}>
                <div className="flex flex-col gap-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[rgba(69,233,248,0.14)]">
                      <img src={imgAirdropBoxStreamlineKameleon} alt="" className="h-6 w-6 object-contain" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-[16px] font-extrabold leading-[1.5] text-white font-[var(--font-jakarta)]">
                        Participate in private liquidity staking to gain access to airdrop opportunities.
                      </span>
                      <span className="text-[12px] font-medium leading-[1.5] text-[#9fa9b6] font-[var(--font-jakarta)]">
                        All selected tokens are test tokens. The more you stake, the higher your chance of receiving airdrops.
                      </span>
                    </div>
                  </div>
                  <div className="flex w-full justify-center">
                    <Link
                      href="/earn"
                      className="flex h-11 w-full items-center justify-center rounded-[10px] bg-[#39e5f5] text-[16px] font-semibold leading-[1.5] text-[#0b0f10] font-[var(--font-jakarta)]"
                    >
                      Join Alpha
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="features-section relative left-1/2 flex w-screen -translate-x-1/2 flex-col items-center gap-12 bg-black px-6 py-14 text-center sm:px-10">
            <div className="flex flex-col gap-2">
              <p className="text-[30px] font-extrabold text-[#f56c6c] font-[var(--font-jakarta)]">
                Public liquidity is extractable
              </p>
              <p className="text-[22px] font-extrabold text-[#1dd0e6] font-[var(--font-jakarta)]">
                ZK-protected liquidity is a market advantage
              </p>
            </div>

            <div className="flex w-full max-w-[1140px] flex-wrap justify-between gap-6">
              {featureCards.map((card) => (
                <div
                  key={card.title}
                  className="feature-card group flex h-full w-full max-w-[260px] min-h-[140px] flex-col gap-3 rounded-[18px] px-3 py-2 transition-colors duration-200 hover:bg-[#0c171c] hover:shadow-[0_20px_70px_rgba(0,0,0,0.4)]"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[rgba(57,229,245,0.08)]">
                      <img src={card.icon} alt="" className="h-5 w-5 object-contain" />
                    </div>
                    <h3 className="text-[16px] font-extrabold leading-[1.3] text-white font-[var(--font-jakarta)]">
                      {card.title}
                    </h3>
                  </div>
                  <p className="text-[14px] leading-[1.5] text-[#b5bbc5] font-[var(--font-jakarta)]">
                    {card.copy}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="stack-section relative left-1/2 flex w-screen -translate-x-1/2 flex-col items-center gap-12 bg-[#111218] px-6 pt-16 pb-16 text-center sm:px-10">
            <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-12">
              <span className="block text-[48px] font-extrabold leading-[150%] text-[color:var(--text-primary-white,#FFF)] text-center font-[var(--font-jakarta)]">
                LP
              </span>
              <span className="stack-arrow block text-[48px] font-extrabold leading-[150%] text-[color:var(--text-primary-white,#FFF)] text-center font-[var(--font-jakarta)]">
                →
              </span>
              <span className="block text-[48px] font-extrabold leading-[150%] text-[color:var(--text-primary-white,#FFF)] text-center font-[var(--font-jakarta)]">
                ZK
                <br />
                Vault
              </span>
              <span className="stack-arrow block text-[48px] font-extrabold leading-[150%] text-[color:var(--text-primary-white,#FFF)] text-center font-[var(--font-jakarta)]">
                →
              </span>
              <span className="block text-[48px] font-extrabold leading-[150%] text-[color:var(--text-primary-white,#FFF)] text-center font-[var(--font-jakarta)]">
                Aggregator
                <br />
                PMMs
              </span>
              <span className="stack-arrow block text-[48px] font-extrabold leading-[150%] text-[color:var(--text-primary-white,#FFF)] text-center font-[var(--font-jakarta)]">
                →
              </span>
              <span className="block text-[48px] font-extrabold leading-[150%] text-[color:var(--text-primary-white,#FFF)] text-center font-[var(--font-jakarta)]">
                Ettlement
                <br />
                model
              </span>
            </div>

            <div className="stack-cards-container flex w-full max-w-[820px] flex-col gap-5">
              {stackCards.map((card) => (
                <div
                  key={card.title}
                  className={`stack-card relative flex min-h-[180px] items-start gap-5 rounded-[18px] px-12 py-5 text-left transition-all duration-200 bg-transparent hover:shadow-[0_18px_60px_rgba(0,0,0,0.45)] ${card.hoverBgClass}`}
                >
                  <div className={`absolute left-4 top-5 bottom-5 w-[4px] ${card.accentClass}`} />
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center p-1">
                    <img src={card.icon} alt="" className="h-full w-full object-contain" />
                  </div>
                  <div className="flex flex-1 flex-col gap-2">
                    <h4 className="text-[22px] font-extrabold leading-[1.5] text-[color:var(--text-primary-white,#ffffff)] font-['Google_Sans_Flex',sans-serif]">
                      {card.title}
                    </h4>
                    <ul className="list-disc space-y-1 ps-5 text-[14px] leading-[1.5] text-[#b0b7c3] font-['Google_Sans_Flex',sans-serif]">
                      {card.copy.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="proof-section relative left-1/2 flex w-screen -translate-x-1/2 flex-col items-center gap-12 bg-[#0c0d13] px-6 py-20 text-center sm:px-10">
            <h2 className="text-[48px] font-extrabold leading-[1.5] text-[color:var(--text-primary-white,#ffffff)] font-['Google_Sans_Flex',sans-serif]">
              Proof of Integrity
            </h2>

            <div className="grid w-full max-w-[940px] grid-cols-1 gap-12 md:grid-cols-2">
              <div className="flex flex-col gap-6 text-left">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[rgba(57,229,245,0.08)]">
                    <img src={img10} alt="" className="h-7 w-7 object-contain" />
                  </div>
                  <p className="text-[24px] font-extrabold leading-[1.5] text-white font-['Google_Sans_Flex',sans-serif]">
                    Audit &amp; Formal Verification
                  </p>
                </div>
                <div className="flex flex-col divide-y divide-[#2b2b2b] rounded-[20px] border border-[#1a2430] bg-[#0b0f16]">
                  {auditPoints.map((point) => (
                    <div key={point} className="audit-point flex items-center gap-4 px-3 py-4 sm:px-4">
                      <img src={img11} alt="" className="h-5 w-5" />
                      <p className="text-[16px] leading-[1.5] text-[#cbcfd6] font-['Google_Sans_Flex',sans-serif]">
                        {point}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-6 text-left">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[rgba(57,229,245,0.08)]">
                    <img src={img12} alt="" className="h-7 w-7 object-contain" />
                  </div>
                  <p className="text-[24px] font-extrabold leading-[1.5] text-white font-['Google_Sans_Flex',sans-serif]">
                    Performance Metrics
                  </p>
                </div>
                <div className="flex flex-col divide-y divide-[#2b2b2b] rounded-[20px] border border-[#1a2430] bg-[#0b0f16]">
                  {metrics.map((metric) => (
                    <div key={metric.label} className="flex items-center justify-between px-3 py-4 sm:px-4">
                      <p className="text-[16px] leading-[1.5] text-[#cbcfd6] font-['Google_Sans_Flex',sans-serif]">
                        {metric.label}
                      </p>
                      <p className="metric-value text-[24px] font-extrabold leading-[1.5] text-[#39e5f5] font-['Google_Sans_Flex',sans-serif]">
                        {metric.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="footer-section relative left-1/2 flex w-screen -translate-x-1/2 flex-col items-center gap-6 bg-[#0c0e13] px-6 py-20 text-center sm:px-10">
            <h3 className="text-[48px] font-extrabold leading-[1.5] text-[color:var(--text-primary-white,#ffffff)] font-['Google_Sans_Flex',sans-serif]">
              Deposit privately. <span className="text-[#39e5f5]">Earn publicly.</span>
            </h3>
            <p className="max-w-[820px] text-[24px] font-medium leading-[1.5] text-[#cbcfd6] font-['Google_Sans_Flex',sans-serif]">
              Deposit liquidity privately via ZKPool and capture institutional-level yields.
            </p>
            <div className="footer-cta flex flex-col items-center gap-3">
              <Link
                href="/earn"
                className="flex h-[54px] items-center justify-center rounded-[8px] bg-[#39e5f5] px-6 text-[16px] font-semibold leading-[1.5] text-[#090909] font-['Google_Sans_Flex',sans-serif]"
              >
                Join Alpha
              </Link>
              <p className="text-[16px] font-medium leading-[1.5] text-[#909090] font-['Google_Sans_Flex',sans-serif]">
                Docs / Github
              </p>
            </div>
          </section>
        </main>
      </div>
    </>
  );
}

export default HomePage;
